/**
 * Carga de un capítulo para el LECTOR, OFFLINE-FIRST.
 *
 * Orden de resolución:
 *   1. Base PowerSync local (SQLite/OPFS) — `bible_verse_content`. Funciona sin
 *      red ni servidor; es la fuente del MVP offline sembrada en el arranque.
 *   2. Comando nativo Tauri (`get_chapter_native`) si corremos dentro de Tauri.
 *   3. API HTTP del servidor Rust (`/api/v1/bible/verses/chapter`) como último
 *      recurso (capítulos fuera de la semilla, p. ej.).
 */

import { and, asc, bible_verse_content, eq, type PowerSyncDrizzleDB } from "@bso/powersync";
import { api } from "@/lib/api";
import type { components } from "@bso/client";

type ChapterVerse = components["schemas"]["VerseResponse"];

/** Lee el capítulo desde la base local. Devuelve [] si no está sembrado. */
export async function loadChapterFromLocal(
  db: PowerSyncDrizzleDB | null,
  version: string,
  book: number,
  chapter: number,
): Promise<ChapterVerse[]> {
  if (!db || Number.isNaN(book) || Number.isNaN(chapter)) {
    return [];
  }
  const rows = await db
    .select({
      version: bible_verse_content.version,
      book: bible_verse_content.book,
      chapter: bible_verse_content.chapter,
      verse: bible_verse_content.verse,
      text: bible_verse_content.text,
    })
    .from(bible_verse_content)
    .where(
      and(
        eq(bible_verse_content.version, version),
        eq(bible_verse_content.book, book),
        eq(bible_verse_content.chapter, chapter),
      ),
    )
    .orderBy(asc(bible_verse_content.verse));

  return rows.flatMap((r) =>
    r.verse === null || r.text === null
      ? []
      : [
          {
            version: r.version ?? version,
            book: r.book ?? book,
            chapter: r.chapter ?? chapter,
            verse: r.verse,
            text: r.text,
          },
        ],
  );
}

/**
 * Carga el capítulo desde el servidor (HTTP) — fallback para capítulos fuera
 * de la semilla local. NO lanza si el servidor no está disponible: devolver []
 * mantiene el lector usable offline (el error boundary raíz no debe aparecer).
 */
async function loadChapterFromServer(
  version: string,
  book: number,
  chapter: number,
): Promise<ChapterVerse[]> {
  try {
    const { data, response } = await api.GET("/api/v1/bible/verses/chapter", {
      params: { query: { version, book, chapter } },
    });
    if (!response.ok) {
      return [];
    }
    return data ?? [];
  } catch (e) {
    console.warn("[reader] No se pudo obtener el capítulo del servidor (offline?)", e);
    return [];
  }
}

/**
 * Resuelve un capítulo offline-first: local → Tauri nativo → API HTTP.
 */
export async function loadChapter(
  db: PowerSyncDrizzleDB | null,
  version: string,
  book: number,
  chapter: number,
): Promise<ChapterVerse[]> {
  const local = await loadChapterFromLocal(db, version, book, chapter);
  if (local.length > 0) {
    return local;
  }

  // Comando nativo Tauri (offline en desktop/mobile fuera de la semilla)
  if (typeof globalThis !== "undefined" && "__TAURI_INTERNALS__" in globalThis) {
    try {
      const { invoke } = await import("@tauri-apps/api/core");
      const nativeVerses = await invoke<ChapterVerse[]>("get_chapter_native", {
        version,
        book,
        chapter,
      });
      if (nativeVerses.length > 0) {
        return nativeVerses;
      }
    } catch (e) {
      console.warn("Failed to get chapter natively, falling back to HTTP api", e);
    }
  }

  return loadChapterFromServer(version, book, chapter);
}
